import {
  DesktopPanelConfig,
  DesktopPopupConfig,
  DesktopStepConfig,
  DesktopStepType,
  MobileDrawerConfig,
  MobileModalConfig,
  MobileStepConfig,
  MobileStepType,
  SpotlightSize,
} from "./spotlight-schema";

export const POPUP_DIMENSIONS: Record<
  SpotlightSize,
  { width: number; height: number }
> = {
  [SpotlightSize.S]: { width: 320, height: 240 },
  [SpotlightSize.M]: { width: 560, height: 240 },
  [SpotlightSize.L]: { width: 720, height: 540 },
};

export const PANEL_WIDTHS: Record<SpotlightSize, number> = {
  [SpotlightSize.S]: 360,
  [SpotlightSize.M]: 480,
  [SpotlightSize.L]: 640,
};

export const DRAWER_HEIGHT_PERCENT: Record<SpotlightSize, number> = {
  [SpotlightSize.S]: 0.3,
  [SpotlightSize.M]: 0.5,
  [SpotlightSize.L]: 0.75,
};

export const MOBILE_WIDTH = 375;
export const MOBILE_HEIGHT = 812;

export interface SpotlightContainerStyles {
  width?: number | string;
  height?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  backgroundColor?: string;
}

export function getPopupContainerStyles(
  config: DesktopPopupConfig,
): SpotlightContainerStyles {
  const dimensions = POPUP_DIMENSIONS[config.size];
  const settings = config.advancedSettings;

  return {
    width: dimensions.width,
    minHeight: dimensions.height,
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    backgroundColor: settings.background,
  };
}

export function getModalContainerStyles(
  config: MobileModalConfig,
): SpotlightContainerStyles {
  const dimensions = POPUP_DIMENSIONS[config.size];
  const settings = config.advancedSettings;

  return {
    width: dimensions.width,
    minHeight: dimensions.height,
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    backgroundColor: settings.background,
  };
}

export function getPanelContainerStyles(
  config: DesktopPanelConfig,
  viewportHeight?: number,
): SpotlightContainerStyles {
  const width =
    config.advancedSettings.width > 0
      ? config.advancedSettings.width
      : PANEL_WIDTHS[config.size];
  const settings = config.advancedSettings;

  return {
    width,
    height: viewportHeight ?? "100%",
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    backgroundColor: settings.background,
  };
}

export function getFullscreenContainerStyles(
  config: DesktopStepConfig | MobileStepConfig,
): SpotlightContainerStyles {
  const settings = config.advancedSettings;

  return {
    width: "100%",
    height: "100%",
    backgroundColor: settings.background,
  };
}

export function getDrawerContainerStyles(
  config: MobileDrawerConfig,
  viewportHeight: number,
): SpotlightContainerStyles {
  const heightPercent = DRAWER_HEIGHT_PERCENT[config.size];
  const height = viewportHeight * heightPercent;
  const settings = config.advancedSettings;

  return {
    width: "100%",
    height,
    maxHeight: height,
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    backgroundColor: settings.background,
  };
}

export function getDesktopContainerStyles(
  config: DesktopStepConfig,
  viewportHeight?: number,
): SpotlightContainerStyles {
  switch (config.type) {
    case DesktopStepType.POPUP:
      return getPopupContainerStyles(config);
    case DesktopStepType.PANEL:
      return getPanelContainerStyles(config, viewportHeight);
    case DesktopStepType.FULLSCREEN:
      return getFullscreenContainerStyles(config);
  }
}

export function getMobileContainerStyles(
  config: MobileStepConfig,
  viewportHeight: number,
): SpotlightContainerStyles {
  switch (config.type) {
    case MobileStepType.DRAWER:
      return getDrawerContainerStyles(config, viewportHeight);
    case MobileStepType.MODAL:
      return getModalContainerStyles(config);
    case MobileStepType.FULLSCREEN:
      return getFullscreenContainerStyles(config);
  }
}

export function getContainerStyles(
  config: DesktopStepConfig | MobileStepConfig,
  isMobile: boolean,
  viewportHeight: number,
): SpotlightContainerStyles {
  if (isMobile) {
    return getMobileContainerStyles(config as MobileStepConfig, viewportHeight);
  }
  return getDesktopContainerStyles(config as DesktopStepConfig, viewportHeight);
}
